<template>
  <div class="ui container view">
    <div class="ui grid">
      <div class="two column row">
        <div class="ten wide column">
          <table class="ui celled definition table">
            <thead>
              <tr>
                <th></th>
                <th colspan="4">Estimate Breakdown</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="component in quoteAggregates.components" v-bind:class="currentComponentClass(component.title)">
                <td class="collapsing">
                  <div class="ui fitted toggle checkbox">
                    <input type="checkbox" checked="checked" tabindex="0" class="hidden" @change="toggleComponent(component.title)">
                  </div>
                </td>
                <td class="title"><strong>{{ component.title }}</strong></td>
                <td class="hours">{{ component.total_hours }}</td>
                <td class="amount">{{ component.total_hours * quoteParams.rate | currency }}</td>
                <td class="amount">{{ component.total_hours * quoteParams.optionalRate | currency }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th></th>
                <th class="title">Subtotal</th>
                <th class="hours">{{ componentsSumBy('hours') }}</th>
                <th class="amount">{{ componentsSumBy('hours') * quoteParams.rate | currency }}</th>
                <th class="amount">{{ componentsSumBy('hours') * quoteParams.optionalRate | currency }}</th>
              </tr>
              <tr>
                <th class="empty"></th>
                <th class="title">Project Management</th>
                <th class="hours">{{ componentsSumBy('pm') }}</th>
                <th class="amount">{{ componentsSumBy('pm') * quoteParams.rate | currency }}</th>
                <th class="amount">{{ componentsSumBy('pm') * quoteParams.optionalRate | currency }}</th>
              </tr>
              <tr>
                <th class="empty"></th>
                <th class="title" colspan="2">Fixed Cost Items</th>
                <th class="amount">{{ fixedCostItemsAmount | currency }}</th>
                <th class="amount">{{ fixedCostItemsAmount | currency }}</th>
              </tr>
              <tr class="total">
                <th class="empty"></th>
                <th class="title">Total</th>
                <th class="hours">{{ componentsSumBy('total_hours') }}</th>
                <th class="amount">{{ (componentsSumBy('total_hours') * quoteParams.rate) + fixedCostItemsAmount | currency }}</th>
                <th class="amount">{{ (componentsSumBy('total_hours') * quoteParams.optionalRate) + fixedCostItemsAmount | currency }}</th>
              </tr>
            </tfoot>
          </table>
          <table class="ui blue celled table" v-show="quoteParams.showHosting">
            <thead>
              <tr>
                <th colspan="3">Monthly Recurring Services</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td class="title">Hosting</td>
                <td class="hours">{{ totalDevHours }} hrs &times; {{ quoteParams.newlineRate | currency }} &times; {{ quoteParams.hostingRatio }}% + {{ quoteParams.hostingBase | currency }}</td>
                <td class="amount">{{ totalHostingAmount | currency }}</td>
              </tr>
              <tr>
                <td class="title">Support</td>
                <td class="hours">{{ totalDevHours }} hrs &times; {{ quoteParams.newlineRate | currency }} &times; {{ quoteParams.supportRatio }}% + {{ quoteParams.supportBase | currency }}</td>
                <td class="amount">{{ totalSupportAmount | currency }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="total">
                <th class="title" colspan="2">Total</th>
                <th class="amount">{{ totalMonthlyServicesAmount | currency }}</th>
              </tr>
            </tfoot>
          </table>
        </div>
        <div class="six wide column">
          <table class="ui red table">
            <thead>
              <tr>
                <th colspan="2">Estimate Parameters</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Project Management Ratio</td>
                <td class="param-input">
                  <div class="ui small fluid labeled input">
                    <label class="ui label">%</label>
                    <input v-model.number="quoteParams.pmRatio" @change="updatePmRatio" type="text">
                  </div>
                </td>
              </tr>
              <tr>
                <td>Hourly Rate</td>
                <td class="param-input">
                  <div class="ui small fluid labeled input">
                    <label class="ui label">$</label>
                    <input v-model.number="quoteParams.rate" @change="updateQuoteParams" type="text">
                  </div>
                </td>
              </tr>
              <tr>
                <td>Optional Hourly Rate</td>
                <td class="param-input">
                  <div class="ui small fluid labeled input">
                    <label class="ui label">$</label>
                    <input v-model.number="quoteParams.optionalRate" @change="updateOptionalRate" type="text">
                  </div>
                </td>
              </tr>
              <tr>
                <td>Show Monthly Recurring Services</td>
                <td class="param-input">
                  <div class="ui toggle checkbox">
                    <input type="checkbox" tabindex="0" class="hidden" v-model="quoteParams.showHosting" @change="toggleShowHosting">
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  data () {
    return {
      quoteParams: {},
      quoteAggregates: {},
      includedComponents: [],
      fixedCostItemsAmount: 0
    }
  },

  computed: {
    hostingRatioPercent () {
      return this.quoteParams.hostingRatio / 100
    },

    supportRatioPercent () {
      return this.quoteParams.supportRatio / 100
    },

    totalDevHours () {
      return this.componentsSumBy('dev')
    },

    totalHostingAmount () {
      return _.ceil((this.totalDevHours * this.quoteParams.newlineRate * this.hostingRatioPercent) + this.quoteParams.hostingBase)
    },

    totalSupportAmount () {
      return _.ceil((this.totalDevHours * this.quoteParams.newlineRate * this.supportRatioPercent) + this.quoteParams.supportBase)
    },

    totalMonthlyServicesAmount () {
      return this.totalHostingAmount + this.totalSupportAmount
    }
  },

  methods: {
    initDashboard () {
      this.quoteParams = this.$store.getters.params
      this.quoteAggregates = this.$store.getters.aggregates
      this.includedComponents = _.map(this.quoteAggregates.components, 'title')
      this.fixedCostItemsAmount = this.quoteAggregates.fixed_cost_items_amount
    },

    currentComponentClass (title) {
      if (_.includes(this.includedComponents, title)) {
        return ''
      } else {
        return 'not-included'
      }
    },

    toggleComponent (title) {
      if (_.includes(this.includedComponents, title)) {
        let index = this.includedComponents.indexOf(title)
        this.includedComponents.splice(index, 1)
      } else {
        this.includedComponents.push(title)
      }
    },

    componentsSumBy (cat) {
      let hours = 0
      var included = this.includedComponents
      var available = this.quoteAggregates.components
      _.forEach(included, function (o) {
        _.forEach(available, function (c) {
          if (o === c.title) {
            hours += parseInt(c[cat])
          }
        })
      })
      return hours
    },

    toggleShowHosting () {
      this.quoteParams.showHosting = !this.quoteParams.showHosting
      this.updateQuoteParams()
    },

    updatePmRatio () {
      this.updateQuoteParams()
      this.quoteAggregates = this.$store.getters.aggregates
    },

    updateQuoteParams () {
      this.$store.commit('UPDATE_QUOTE_PARAMS', {
        rate: this.quoteParams.rate,
        pmRatio: this.quoteParams.pmRatio,
        showHosting: this.quoteParams.showHosting
      })
    },

    updateOptionalRate () {
      this.$store.commit('UPDATE_OPTIONAL_RATE', this.quoteParams.optionalRate)
    }
  },

  created () {
    if (this.$store.getters.quoteIsLoaded) {
      this.initDashboard()
    } else {
      this.$router.replace({ name: 'new' })
    }
  },

  mounted () {
    window.$('.ui.checkbox').checkbox()
  },

  updated () {
    window.$('.ui.checkbox').checkbox()
  }
}
</script>

<style lang="scss" scoped>
  .ui.table {
    thead tr.component th {
      background-color: #646464;
      color: #f0f0f0;
    }

    tr {
      &.total {
        th {
          background-color: #646464;
          color: #f0f0f0;
          font-weight: 700;
        }
      }

      &.not-included {
        td {
          color: rgba(40, 40, 40, .3);
        }
      }

      th,
      td {
        &.hours {
          text-align: center;
        }

        &.amount {
          text-align: right;
        }

        &.param-input {
          text-align: right;
          width: 110px;

          input[type=text] {
            text-align: right;
          }
        }
      }
    }

    tfoot tr th.empty {
      border-top: 0;
    }
  }
</style>



// WEBPACK FOOTER //
// Dashboard.vue?f7e9da86